/**
 * Description: Systems Inventory page with sorting, pagination, filtering, persistent preferences, and view action
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Search,
  Edit,
  Trash2,
  Plus,
  Copy,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
} from 'lucide-react'
import { api, System } from '@services/api'
import { toast } from 'react-toastify'

type SortField =
  | 'system_name'
  | 'business_owner'
  | 'management_type'
  | 'product_count'
  | 'security_score'
  | 'compliance_score'
  | 'updated_at'
type SortDirection = 'asc' | 'desc'

const Systems: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [systems, setSystems] = useState<System[]>([])
  const [filteredSystems, setFilteredSystems] = useState<System[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  // Read product count filter from URL
  const productCountMin = searchParams.get('productCountMin')
  const productCountMax = searchParams.get('productCountMax')

  // Load sort preferences from localStorage
  const [sortField, setSortField] = useState<SortField>(() => {
    const saved = localStorage.getItem('systemsInventory_sortField')
    return (saved as SortField) || 'system_name'
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    const saved = localStorage.getItem('systemsInventory_sortDirection')
    return (saved as SortDirection) || 'asc'
  })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  useEffect(() => {
    loadSystems()
  }, [])

  // Save sort preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('systemsInventory_sortField', sortField)
    localStorage.setItem('systemsInventory_sortDirection', sortDirection)
  }, [sortField, sortDirection])

  useEffect(() => {
    let filtered = systems

    // Apply product count filter if present
    if (productCountMin !== null && productCountMax !== null) {
      const min = parseInt(productCountMin)
      const max = parseInt(productCountMax)
      filtered = filtered.filter((system) => {
        const count = system.product_count || 0
        return count >= min && count <= max
      })
    }

    // Apply search query filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (system) =>
          system.system_name?.toLowerCase().includes(query) ||
          system.description?.toLowerCase().includes(query) ||
          system.business_owner?.toLowerCase().includes(query) ||
          system.technical_product_owner?.toLowerCase().includes(query),
      )
    }

    setFilteredSystems(filtered)
    setCurrentPage(1) // Reset to first page when filters change
  }, [searchQuery, systems, productCountMin, productCountMax])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field with ascending direction
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedSystems = [...filteredSystems].sort((a, b) => {
    let aValue: any = a[sortField]
    let bValue: any = b[sortField]

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) {
      aValue = ''
    }
    if (bValue === null || bValue === undefined) {
      bValue = ''
    }

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase()
    }
    if (typeof bValue === 'string') {
      bValue = bValue.toLowerCase()
    }

    // For dates
    if (sortField === 'updated_at') {
      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0
      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime
    }

    // Compare
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1
    }
    return 0
  })

  // Pagination
  const totalPages = Math.ceil(sortedSystems.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedSystems = sortedSystems.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const loadSystems = async () => {
    try {
      setIsLoading(true)
      const data = await api.getAllSystems()
      setSystems(data)
      setFilteredSystems(data)
    } catch (error) {
      toast.error('Failed to load systems')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id: number, systemName: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent row click when deleting

    if (!window.confirm(`Are you sure you want to delete "${systemName}"?`)) {
      return
    }

    try {
      await api.deleteSystem(id)
      toast.success('System deleted successfully')
      loadSystems()
    } catch (error) {
      toast.error('Failed to delete system')
      console.error(error)
    }
  }

  const handleClone = async (id: number, systemName: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent row click when cloning

    if (!window.confirm(`Clone "${systemName}"?`)) {
      return
    }

    try {
      const result = await api.cloneSystem(id)
      toast.success('System cloned successfully')
      loadSystems()
      // Navigate to edit the cloned system
      navigate(`/systems/${result.id}/edit`)
    } catch (error) {
      toast.error('Failed to clone system')
      console.error(error)
    }
  }

  const getScoreBadgeColor = (score?: string) => {
    switch (score) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
      case 'low':
        return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '-'
    }
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const SortableHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <th
      className="cursor-pointer px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase select-none hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <ArrowUpDown
          className={`h-4 w-4 ${sortField === field ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500'}`}
        />
      </div>
    </th>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Systems</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {filteredSystems.length} {filteredSystems.length === 1 ? 'system' : 'systems'} found
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/systems/new')}
          className="btn-primary flex cursor-pointer items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add System</span>
        </button>
      </div>

      {/* Active Filter Indicator */}
      {productCountMin !== null && productCountMax !== null && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800/50 dark:bg-blue-900/30">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
              Active Filter:{' '}
              {productCountMax === '999999'
                ? '20+ Products'
                : productCountMax === '19'
                  ? '5 to 20 Products'
                  : '0 to 5 Products'}
            </span>
          </div>
          <button
            onClick={() => {
              setSearchParams({})
              navigate('/systems')
            }}
            className="flex items-center space-x-1 rounded px-3 py-1 text-sm text-blue-700 transition-colors hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/40"
          >
            <X className="h-4 w-4" />
            <span>Clear Filter</span>
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="card">
        <div className="relative">
          <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search systems by name, description, owner..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Systems Table */}
      {isLoading ? (
        <div className="card py-12 text-center">
          <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading systems...</p>
        </div>
      ) : filteredSystems.length === 0 ? (
        <div className="card py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {searchQuery ? 'No systems found matching your search.' : 'No systems yet. Create your first one!'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-white/5">
                <tr>
                  <SortableHeader field="system_name" label="System Name" />
                  <SortableHeader field="business_owner" label="Business Owner" />
                  <SortableHeader field="management_type" label="Management" />
                  <SortableHeader field="product_count" label="Product Count" />
                  <SortableHeader field="security_score" label="Security" />
                  <SortableHeader field="compliance_score" label="Compliance" />
                  <SortableHeader field="updated_at" label="Last Updated" />
                  <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {paginatedSystems.map((system) => (
                  <tr key={system.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-900">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{system.system_name}</div>
                        {system.description && (
                          <div className="max-w-xs truncate text-sm text-gray-500 dark:text-gray-400">
                            {system.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                      {system.business_owner || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {system.management_type && (
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs leading-5 font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {system.management_type}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs leading-5 font-semibold text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                        {system.product_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {system.security_score && (
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs leading-5 font-semibold ${getScoreBadgeColor(system.security_score)}`}
                        >
                          {system.security_score}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {system.compliance_score && (
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs leading-5 font-semibold ${getScoreBadgeColor(system.compliance_score)}`}
                        >
                          {system.compliance_score}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                      {formatDate(system.updated_at)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/systems/${system.id}`)
                          }}
                          className="cursor-pointer rounded p-2 text-indigo-600 hover:text-indigo-900 dark:hover:text-indigo-300"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/systems/${system.id}/edit`)
                          }}
                          className="cursor-pointer rounded p-2 text-blue-600 hover:text-blue-900 dark:hover:text-blue-300"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleClone(system.id!, system.system_name, e)}
                          className="cursor-pointer rounded p-2 text-green-600 hover:text-green-900 dark:hover:text-green-300"
                          title="Clone"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(system.id!, system.system_name, e)}
                          className="cursor-pointer rounded p-2 text-red-600 hover:text-red-900 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, sortedSystems.length)}</span> of{' '}
                    <span className="font-medium">{sortedSystems.length}</span> results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm dark:shadow-none"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex cursor-pointer items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current page
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex cursor-pointer items-center border px-4 py-2 text-sm font-medium ${
                              page === currentPage
                                ? 'bg-primary-50 border-primary-500 text-primary-600 z-10'
                                : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return (
                          <span
                            key={page}
                            className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                          >
                            ...
                          </span>
                        )
                      }
                      return null
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex cursor-pointer items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Systems
